import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCardsStore, useTopicsStore } from '@/stores'
import { formatLidForDisplay } from '@/utils/lid'
import type { Card, CardTreeNode } from '@/types'
import './InboxPage.css'

export default function InboxPage() {
  const navigate = useNavigate()
  const { getInboxCards, placeInboxCard, deleteCard, getCardTree } = useCardsStore()
  const { getTopicsSorted } = useTopicsStore()

  const inboxCards = getInboxCards()
  const sortedTopics = getTopicsSorted()

  const [selectedCard, setSelectedCard] = useState<Card | null>(null)
  const [isPlacing, setIsPlacing] = useState(false)
  const [placeError, setPlaceError] = useState<string | null>(null)

  // Delete confirmation state
  const [cardToDelete, setCardToDelete] = useState<Card | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Placement form state
  const [topicId, setTopicId] = useState('')
  const [parentLid, setParentLid] = useState('')

  // 簡化規則：有選 parent → branch，無選 parent → continue
  const branchType: 'branch' | 'continue' = parentLid ? 'branch' : 'continue'

  const handlePlaceCard = async () => {
    if (!selectedCard || !topicId) return

    setIsPlacing(true)
    setPlaceError(null)

    try {
      const placedCard = await placeInboxCard(selectedCard.lid, {
        topicId,
        parentLid: parentLid || null,
        branchType,
      })
      setSelectedCard(null)
      setTopicId('')
      setParentLid('')
      navigate(`/card/${encodeURIComponent(placedCard.lid)}`)
    } catch (error) {
      setPlaceError(error instanceof Error ? error.message : 'Failed to place card')
    } finally {
      setIsPlacing(false)
    }
  }

  const handleDeleteCard = async () => {
    if (!cardToDelete) return

    setIsDeleting(true)
    setDeleteError(null)

    try {
      await deleteCard(cardToDelete.lid)
      setCardToDelete(null)
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete card')
    } finally {
      setIsDeleting(false)
    }
  }

  const cardTree = topicId ? getCardTree(topicId) : []

  return (
    <div className="inbox-page">
      <div className="page-header">
        <div>
          <h1>Inbox</h1>
          <p className="text-secondary">
            {inboxCards.length} card{inboxCards.length !== 1 ? 's' : ''} waiting to be placed
          </p>
        </div>
        <Link to="/card/new?inbox=true" className="btn btn-primary">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Quick Capture
        </Link>
      </div>

      {inboxCards.length === 0 ? (
        <div className="empty-state">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
            <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
          </svg>
          <h3>Inbox is empty</h3>
          <p>Quick capture ideas and place them later in your knowledge tree.</p>
          <Link to="/card/new?inbox=true" className="btn btn-primary">
            Capture a thought
          </Link>
        </div>
      ) : (
        <div className="inbox-layout">
          <div className="inbox-list">
            {inboxCards.map((card) => (
              <div
                key={card.lid}
                className={`inbox-card card ${selectedCard?.lid === card.lid ? 'selected' : ''}`}
                onClick={() => setSelectedCard(card)}
              >
                <div className="inbox-card-header">
                  <h3>{card.title}</h3>
                  <span className="inbox-card-date">
                    {new Date(card.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {card.content && (
                  <p className="inbox-card-preview">
                    {card.content.slice(0, 150)}
                    {card.content.length > 150 ? '...' : ''}
                  </p>
                )}
                <div className="inbox-card-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedCard(card)
                    }}
                  >
                    Place
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      setCardToDelete(card)
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {selectedCard && (
            <div className="place-panel card">
              <h3>Place Card</h3>
              <p className="text-secondary text-sm mb-md">
                Choose where to place "{selectedCard.title}" in your knowledge tree.
              </p>

              <div className="form-group">
                <label htmlFor="topic">Topic</label>
                <select
                  id="topic"
                  className="input"
                  value={topicId}
                  onChange={(e) => {
                    setTopicId(e.target.value)
                    setParentLid('')
                  }}
                  required
                >
                  <option value="">Select a topic...</option>
                  {sortedTopics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.id}/ - {topic.name}
                    </option>
                  ))}
                </select>
              </div>

              {topicId && cardTree.length > 0 && (
                <>
                  <div className="form-group">
                    <label>Place after</label>
                    <div className="parent-selection">
                      <button
                        type="button"
                        className={`parent-option ${!parentLid ? 'selected' : ''}`}
                        onClick={() => setParentLid('')}
                      >
                        <span className="lid-badge">New Root</span>
                        <span>Start a new sequence</span>
                      </button>
                      <div className="parent-tree">
                        <SelectableTree
                          nodes={cardTree}
                          selectedLid={parentLid}
                          onSelect={setParentLid}
                        />
                      </div>
                    </div>
                  </div>

                </>
              )}

              {placeError && (
                <div className="form-error">{placeError}</div>
              )}

              <div className="form-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedCard(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handlePlaceCard}
                  disabled={!topicId || isPlacing}
                >
                  {isPlacing ? 'Placing...' : 'Place Card'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {cardToDelete && (
        <div className="modal-overlay" onClick={() => !isDeleting && setCardToDelete(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Card</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this inbox card?</p>
              <p className="text-secondary text-sm">"{cardToDelete.title}"</p>
              {deleteError && (
                <div className="form-error">{deleteError}</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setCardToDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteCard}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Selectable tree component for parent selection
interface SelectableTreeProps {
  nodes: CardTreeNode[]
  selectedLid: string
  onSelect: (lid: string) => void
  level?: number
}

function SelectableTree({ nodes, selectedLid, onSelect, level = 0 }: SelectableTreeProps) {
  return (
    <div className="selectable-tree">
      {nodes.map((node) => (
        <div key={node.card.lid} className="selectable-item">
          <button
            type="button"
            className={`parent-option ${selectedLid === node.card.lid ? 'selected' : ''}`}
            onClick={() => onSelect(node.card.lid)}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
          >
            <span className="lid-badge">{formatLidForDisplay(node.card.lid)}</span>
            <span className="option-title">{node.card.title}</span>
          </button>
          {node.children.length > 0 && (
            <SelectableTree
              nodes={node.children}
              selectedLid={selectedLid}
              onSelect={onSelect}
              level={level + 1}
            />
          )}
        </div>
      ))}
    </div>
  )
}
